import React, { useEffect, useRef, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

const FleetVehicleLayer = ({ vehicles, campaignName, arrowColor, isRoutesOnlyMode, staticPoints, onOpenStaticPointPopup, hideNearestPoint }) => {
  const iconCacheRef = useRef(new Map());
  const [arrowSvg, setArrowSvg] = useState(null);

  const baseIconXY = [40, 40];
  const arrowIconXY = [28, 28];
  const compositeIconXY = [54, 54];

  useEffect(() => {
    setArrowSvg(null);
    if (!campaignName) return;

    const url = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/arrow.svg`;
    fetch(url)
      .then((r) => (r.ok ? r.text() : null))
      .then((txt) => {
        if (typeof txt === 'string' && txt.includes('<svg')) {
          setArrowSvg(txt);
        }
      })
      .catch(() => {
        // Keep null; we'll fall back to <img>
      });
  }, [campaignName]);

  useEffect(() => {
    // If the arrow SVG loads later, rebuild icons.
    iconCacheRef.current.clear();
  }, [arrowSvg]);

  const getFleetIcon = (iconName, headingDegrees, resolvedArrowColor) => {
    if (!iconName || !campaignName) return null;

    const heading = Number(headingDegrees);
    const safeHeading = Number.isFinite(heading) ? ((heading % 360) + 360) % 360 : 0;
    const bucket = Math.round(safeHeading / 10) * 10;

    const colorKey = typeof resolvedArrowColor === 'string' ? resolvedArrowColor : 'default';
    const cacheKey = `${iconName}|${bucket}|${colorKey}`;
    const cached = iconCacheRef.current.get(cacheKey);
    if (cached) return cached;

    const baseIconUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/${iconName}`;
    const arrowIconUrl = `${process.env.PUBLIC_URL || ''}/campaigns/${campaignName}/images/arrow.svg`;

    const arrowInnerHtml = arrowSvg
      ? arrowSvg
      : `<img src="${arrowIconUrl}" style="width: 100%; height: 100%;" />`;

    const html = `
      <div style="position: relative; width: ${compositeIconXY[0]}px; height: ${compositeIconXY[1]}px; overflow: visible;">
        <div style="position: absolute; left: 0; top: 0; width: ${baseIconXY[0]}px; height: ${baseIconXY[1]}px; display: flex; align-items: center; justify-content: center;">
          <img src="${baseIconUrl}" style="width: 100%; height: 100%;" />
        </div>
        <div style="position: absolute; right: 0; bottom: 0; width: ${arrowIconXY[0]}px; height: ${arrowIconXY[1]}px; display: flex; align-items: center; justify-content: center; color: ${typeof resolvedArrowColor === 'string' ? resolvedArrowColor : (typeof arrowColor === 'string' ? arrowColor : '#dc3545')}; transform: rotate(${bucket}deg); transform-origin: 50% 50%; z-index: 999;">
          ${arrowInnerHtml}
        </div>
      </div>
    `;

    const icon = L.divIcon({
      html,
      className: '',
      iconSize: [compositeIconXY[0], compositeIconXY[1]],
      iconAnchor: [Math.round(compositeIconXY[0] / 2), Math.round(compositeIconXY[1] / 2)],
      popupAnchor: [0, -Math.round(compositeIconXY[1] / 2)]
    });

    iconCacheRef.current.set(cacheKey, icon);
    return icon;
  };

  const safeStaticPoints = Array.isArray(staticPoints) ? staticPoints : [];

  const findNearestStaticPoint = (position, routeId) => {
    if (!position || position.length !== 2) return null;
    if (!safeStaticPoints.length) return null;

    const lat = Number(position[0]);
    const lng = Number(position[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const routeKey = routeId != null ? String(routeId) : null;
    const routeMatchedPoints = routeKey
      ? safeStaticPoints.filter((pt) => {
        if (!pt) return false;
        const corr = pt.cooresponding_route;
        if (corr == null) return false;
        return String(corr) === routeKey;
      })
      : [];

    const candidates = routeMatchedPoints.length > 0 ? routeMatchedPoints : safeStaticPoints;

    let best = null;
    let bestScore = Infinity;
    for (const pt of candidates) {
      if (!pt) continue;
      const pLat = Number(pt.lat);
      const pLng = Number(pt.lng);
      if (!Number.isFinite(pLat) || !Number.isFinite(pLng)) continue;
      const dLat = lat - pLat;
      const dLng = lng - pLng;
      const score = (dLat * dLat) + (dLng * dLng);
      if (score < bestScore) {
        bestScore = score;
        best = pt;
      }
    }
    return best;
  };

  return (
    <>
      {(vehicles || []).map((vehicle) => {
        const position = vehicle.currentPosition;
        if (!position) return null;

        const routeIdText = vehicle.routeId != null ? String(vehicle.routeId) : '';
        const routeNameText = (vehicle.route && (vehicle.route.short_name || vehicle.route.name))
          ? String(vehicle.route.short_name || vehicle.route.name)
          : '';

        const normalizedName = routeNameText.trim();
        const normalizedId = routeIdText.trim();
        const nameAlreadyHasIdPrefix = Boolean(
          normalizedName
          && normalizedId
          && (normalizedName === normalizedId || normalizedName.startsWith(`${normalizedId} `))
        );

        const routeDisplay = (normalizedId && normalizedName)
          ? (nameAlreadyHasIdPrefix ? normalizedName : `${normalizedId} ${normalizedName}`)
          : (normalizedName || normalizedId || 'Unknown');

        const resolvedArrowColor = typeof vehicle.arrowColor === 'string' ? vehicle.arrowColor : arrowColor;
        const icon = getFleetIcon(vehicle.icon, vehicle.heading, resolvedArrowColor);

        return (
          <Marker
            key={`${vehicle.routeId || 'route'}-${vehicle.id}`}
            position={position}
            icon={icon || undefined}
            vehicleId={vehicle.id}
          >
            <Popup {...(isRoutesOnlyMode ? { autoPan: false, keepInView: false } : {})}>
              {isRoutesOnlyMode ? (
                <div style={{ minWidth: '220px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Route</strong>: {routeDisplay}
                  </div>

                  <div style={{ marginBottom: '8px' }}>
                    <strong>Destination</strong>: {vehicle.tripData && vehicle.tripData.trip_headsign ? vehicle.tripData.trip_headsign : 'Unknown'}
                  </div>

                  {!hideNearestPoint && (
                  <div style={{ marginBottom: '0px' }}>
                    <strong>Nearest Beer</strong>:{' '}
                    {(() => {
                      const nearest = findNearestStaticPoint(position, vehicle.routeId);
                      if (!nearest) return 'Unknown';

                      const label = nearest.title || nearest.name || nearest.id || 'View';
                      return (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (typeof onOpenStaticPointPopup === 'function') {
                              onOpenStaticPointPopup(nearest);
                            }
                          }}
                          style={{ color: '#0d6efd', textDecoration: 'none', fontWeight: 700, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                        >
                          {label}
                        </button>
                      );
                    })()}
                  </div>
                  )}
                </div>
              ) : (
                <div style={{ minWidth: '200px' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>
                    Vehicle {vehicle.id}
                  </h4>
                  {vehicle.routeId && (
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Route:</strong> {vehicle.routeId}
                    </div>
                  )}
                </div>
              )}
            </Popup>
          </Marker>
        );
      })}
    </>
  );
};

export default FleetVehicleLayer;
